
import { Component, Watch, Prop } from "vue-property-decorator";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import { getTimeScales } from "@/utils/chartUtils";
import ThemeUpdatingView from "@/components/general/ThemeUpdatingView.vue";
import { yAxisInteractionLine } from "@/plugins/chartjs";

@Component
export default class BotBalanceOverTimeChart extends ThemeUpdatingView {
  // 📦 The bot balance over time dataset
  @Prop()
  public botBalanceDataset!: number[];

  // 📦 The token price over time dataset
  @Prop()
  public tokenPriceDataset!: number[];

  // 📦 Dates for each portfolio check-up
  @Prop()
  public dateLabels!: Date[];

  // 📦 The selected porfolio chart filter in days
  @Prop()
  public selectedFilterInDays!: number;

  /**
   * 📦📈 Chart which displays the bot balance over time
   */
  private botPerformanceChart: Chart | null = null;

  /**
   * 👂 Listener used in order to determine the selected chart filter mode
   */
  @Watch("tokenPriceDataset")
  private onDaysFilterChanged(val: number[], oldVal?: number[]) {
    this.createChart();
  }

  protected themeUpdate() {
    this.createChart();
  }

  /**
   * 🎨 Draws a dynamic chart on the canvas which is responsible for displaying the porfolio
   */
  private createChart() {
    if (this.botPerformanceChart == null) {
      let container: any = document.getElementById(
        "container-bot-performance-chart"
      );
      this.botPerformanceChart = new Chart(container, {
        type: "line",
        data: {
          labels: this.dateLabels,
          datasets: this.getDatasets(),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: getTimeScales(this.$vuetify, this.selectedFilterInDays == 1),
            y: this.getYAxes(),
          },
          interaction: {
            intersect: false,
          },
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: {
                boxWidth: 12,
                boxHeight: 1,
                font: { family: "Aeroport" },
              },
            },
            tooltip: {
              mode: "index",
              titleFont: { family: "Aeroport" },
              bodyFont: { family: "Aeroport" },
              callbacks: {
                label: (context: any) => {
                  return (
                    " " +
                    context.dataset.label +
                    ": " +
                    Number(context.parsed.y).toFixed(2) +
                    "%"
                  );
                },
              },
            },
          },
        },
        plugins: [yAxisInteractionLine(this.$vuetify)],
      });
    } else {
      this.botPerformanceChart.data.datasets = this.getDatasets();
      this.botPerformanceChart.data.labels = this.dateLabels;
      this.botPerformanceChart.config.options!!.scales!!.y = this.getYAxes();
      this.botPerformanceChart.options.scales!!.x = getTimeScales(
        this.$vuetify,
        this.selectedFilterInDays == 1
      );
      this.botPerformanceChart.update();
    }
  }

  /**
   * 🔨 Calculates a dynamic y axis based on the provided dataset
   */
  private getYAxes(): any {
    // Minimal value of the dataset
    let minValue: number = Math.min(
      ...this.botBalanceDataset,
      ...this.tokenPriceDataset
    );

    // Maximum value of the dataset
    let maxValue: number = Math.max(
      ...this.botBalanceDataset,
      ...this.tokenPriceDataset
    );

    // Boundary which is equal to 5% of the difference between the maximum and minimum value of the dataset
    let chartBoundary: number =
      maxValue - minValue == 0
        ? Math.ceil(maxValue * 0.05)
        : Math.ceil((maxValue - minValue) * 0.05);

    // Minimum y value that should be displayed on the chart
    let minDisplayValue: number = this.getMinDisplayValue(
      minValue,
      maxValue,
      chartBoundary
    );

    // Maximum y value that should be displayed on the chart
    let maxDisplayValue: number = this.getMaxDisplayValue(
      minValue,
      maxValue,
      chartBoundary
    );

    // Step size which is equal to 15% of the difference between the maximum and minimum display value of the dataset
    let stepSize: number =
      maxDisplayValue - minDisplayValue == 0
        ? 1
        : (maxDisplayValue - minDisplayValue) * 0.15;

    return {
      ticks: {
        maxTicksLimit: 8,
        min: minDisplayValue,
        max: maxDisplayValue,
        stepSize: stepSize,
        callback: function(value: number, index: number, values: number[]) {
          return value.toFixed(2) + "%";
        },
        color: this.$vuetify.theme.currentTheme.chartLabel,
        font: {
          family: "Aeroport",
        },
      },
      grid: {
        color: (context: any) =>
          context.tick.value == 0
            ? this.$vuetify.theme.currentTheme.chartGridBaseLineColor
            : this.$vuetify.theme.currentTheme.chartGridColor,
        lineWidth: (context: any) => (context.tick.value == 0 ? 1.2 : 1),
        borderColor: this.$vuetify.theme.currentTheme.chartGridColor,
      },
      stacked: false,
    };
  }

  private getDatasets(): any[] {
    let dataSets: any[] = [];

    // Create gradient fill
    var gradient = (this.$refs.containerBotPerformanceChart as any)
      .getContext("2d")
      .createLinearGradient(0, 0, 0, 600);

    gradient.addColorStop(0, "#A4d7A6");
    gradient.addColorStop(1, "#A6A8F5AB");

    dataSets.push({
      label: "Bot ROI",
      data: this.botBalanceDataset,
      borderColor: this.$vuetify.theme.currentTheme.primary,
      backgroundColor: this.$vuetify.theme.currentTheme.primary,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 5,
      borderWidth: 2,
      tension: 0.5,
    });

    dataSets.push({
      label: "Market ROI",
      data: this.tokenPriceDataset,
      borderColor: this.$vuetify.theme.currentTheme.marketChartColor,
      fill: false,
      backgroundColor: this.$vuetify.theme.currentTheme.marketChartColor,
      pointRadius: 0,
      pointHoverRadius: 5,
      borderWidth: 2,
      tension: 0.5,
    });

    return dataSets;
  }

  /**
   * 🏗 Calculates and returns the minimum display value
   */
  private getMinDisplayValue(
    minValue: number,
    maxValue: number,
    chartBoundary: number
  ) {
    if (maxValue - minValue < 1) {
      return minValue + -chartBoundary > 0 ? minValue + -chartBoundary : 0;
    }

    return minValue;
  }

  /**
   * 🏗 Calculates and returns the maximum display value
   */
  private getMaxDisplayValue(
    minValue: number,
    maxValue: number,
    chartBoundary: number
  ) {
    if (maxValue <= 0) {
      return 5;
    }

    if (maxValue - minValue < 1) {
      return maxValue + chartBoundary > 0 ? maxValue + chartBoundary : 0;
    }

    return maxValue;
  }
}
